// File: src/hooks/useNotifications.js

import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useGlobalToast } from "components/GlobalToastProvider";
import {
  getNotifications,
  markNotificationsRead,
  markAllNotificationsRead,
} from "../api/notifications";

export const useNotifications = (userId) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [currentListLength, setCurrentListLength] = useState(5);
  const [loadingMore, setLoadingMore] = useState(false);
  const limit = 5;
  const { showToast } = useGlobalToast();
  const history = useHistory();

  const load = async (reset = false, poll = false) => {
    const fetchOffset = poll || reset ? 0 : notifications.length;

    const { data } = await getNotifications(userId, fetchOffset, limit);

    setNotifications((prev) => {
      if (poll) {
        const newIds = new Set(data.map((n) => n.id));
        const rest = prev.filter((n) => !newIds.has(n.id));
        return [...data, ...rest];
      } else if (reset) {
        return data;
      } else {
        return [...prev, ...data];
      }
    });

    if (!poll) {
      setHasMore(data.length === limit);
    }
  };

  useEffect(() => {
    if (!userId) return;
    load(true);
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    const pollingInterval = setInterval(() => {
      load(false, true);
    }, 3000);
    return () => clearInterval(pollingInterval);
  }, [userId, currentListLength]);

  useEffect(() => {
    setUnreadCount(notifications.filter((n) => !n.is_read).length);
  }, [notifications]);

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
    load(true);
    setCurrentListLength(5);
  };

  const handleMarkAll = async () => {
    await markAllNotificationsRead(userId);
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    showToast({ message: "Notifications have been marked as read" });
  };

  const handleClick = async (n) => {
    if (!n.is_read) {
      await markNotificationsRead(userId, [n.id]);
      setNotifications((prev) => prev.map((x) => (x.id === n.id ? { ...x, is_read: true } : x)));
    }

    // Handle navigation based on notification type
    if (n.type === "clinical") {
      const metadata = n.metadata || {};
      let redirectUrl = null;

      switch (metadata.action) {
        case "client_assigned":
          redirectUrl = `/clients/${metadata.client_id}/dashboard`;
          break;
        case "schedule_assessment":
          redirectUrl = `/clinician-schedule/create-event?name=${encodeURIComponent(metadata.client_name || "")}&serviceType=ASSESSMENT`;
          break;
        case "schedule_treatment_plan_review":
          redirectUrl = `/clinician-schedule/create-event?name=${encodeURIComponent(metadata.client_name || "")}&serviceType=ASSESSMENT`;
          break;
        case "auth_submitted":
          redirectUrl = `/clients/${metadata.client_id}/insurance#insurance-authorizations`;
          break;
        case "auth_approved":
          redirectUrl = `/clients/${metadata.client_id}/insurance#insurance-authorizations`;
          break;
        case "upload_treatment_plan":
          redirectUrl = `/clients/${metadata.client_id}/documents?uploadDocType=TREATMENT_PLAN`;
          break;
      }

      if (redirectUrl) {
        history.push(redirectUrl);
        handleClose();
      }
    } else if (n.metadata?.redirect_url) {
      // For non-clinical notifications, use redirect_url if provided
      history.push(`/${n.metadata.redirect_url}`);
      handleClose();
    }
  };

  const handleLoadMore = async () => {
    setLoadingMore(true);
    setCurrentListLength((prev) => prev + 5); // Increase the limit for next fetch
    await load();
    setLoadingMore(false);
  };

  if (!userId) {
    return {
      notifications: [],
      unreadCount: 0,
      anchorEl: null,
      hasMore: false,
      loadingMore: false,
      handlers: {
        handleOpen: () => {},
        handleClose: () => {},
        handleMarkAll: () => {},
        handleClick: () => {},
        handleLoadMore: () => {},
      },
    };
  }

  return {
    notifications,
    unreadCount,
    anchorEl,
    hasMore,
    loadingMore,
    handlers: {
      handleOpen,
      handleClose,
      handleMarkAll,
      handleClick,
      handleLoadMore,
    },
  };
};
